import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { LegendList } from '@legendapp/list';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import CutCard from '@/components/General/CutCard';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import ActionButton from '@/components/General/ActionButton';
import BibleStudyNote, { formatStudyDate } from './BibleStudyNote';

const AttendanceStat = ({ label, value }) => (
    <View style={styles.stat}>
        <Text style={styles.statValue}>{value}</Text>
        <Text style={styles.statLabel}>{label}</Text>
    </View>
);

export const BibleStudyStudentCard = ({ student, notes, loggedInUser, canReadNotes, loadingNotes, notesError }) => {
    const router = useRouter();
    const person = student.Person;
    const name = [person?.firstName, person?.lastName].filter(Boolean).join(' ') || 'Student';
    return (
        <CutCard testID={`BibleStudyStudent-${student.personID}`} accentColor="groups" style={styles.card} contentStyle={styles.cardContent}>
            <View style={styles.header}>
                <Avatar profile={person} size={48} />
                <View style={styles.identity}>
                    <Pressable testID={`BibleStudyStudentProfile-${student.personID}`} accessibilityRole="link" accessibilityLabel={`View ${name}'s profile`} onPress={() => router.push(`/people/${student.personID}`)}>
                        <Text style={styles.name}>{name}</Text>
                    </Pressable>
                    <Text style={styles.membership}>{student.isCurrentMember ? 'Current student' : 'Past participant'}</Text>
                </View>
                {canReadNotes && <View style={styles.noteCount}><Text style={styles.noteCountText}>{notes.length} {notes.length === 1 ? 'note' : 'notes'}</Text></View>}
            </View>
            <View style={styles.attendance}>
                <AttendanceStat label="Sessions attended" value={student.attendanceCount} />
                <AttendanceStat label="Last attended" value={student.lastAttendedAt ? formatStudyDate(student.lastAttendedAt) : 'Not yet'} />
                <AttendanceStat label="First attended" value={student.firstAttendedAt ? formatStudyDate(student.firstAttendedAt) : 'Not yet'} />
            </View>
            <View style={styles.notes}>
                <View style={styles.notesHeading}>
                    <Unicon name="document-text-outline" size={15} colorVariant="groups" />
                    <Text style={styles.notesLabel}>Study notes</Text>
                </View>
                {!canReadNotes ? <Text style={styles.empty}>Notes are visible to assigned teachers and managers.</Text>
                    : loadingNotes ? <Text style={styles.empty}>Loading notes...</Text>
                        : notesError ? <Text style={styles.empty}>Notes could not be loaded. Pull to refresh and try again.</Text>
                            : notes.length ? notes.map(note => (
                                <View key={note.id} style={styles.timelineEntry}>
                                    <View style={styles.timelineDot} />
                                    <BibleStudyNote note={note} kind="people" loggedInUser={loggedInUser} showSessionDate />
                                </View>
                            )) : <Text style={styles.empty}>No student notes for this study yet. Add one when logging a session.</Text>}
            </View>
        </CutCard>
    );
};

const BibleStudyStudents = ({ smallGroupID, canReadNotes = false }) => {
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const studentsQuery = Query.bibleStudies.students({ smallGroupID });
    const notesQuery = Query.bibleStudies.peopleNotes({ smallGroupID, options: { enabled: !!smallGroupID && canReadNotes } });
    const [search, setSearch] = useState('');
    const students = studentsQuery.data || [];
    const notesByPerson = useMemo(() => {
        const result = new Map();
        if (canReadNotes) {
            (notesQuery.data || []).forEach(note => {
                const notes = result.get(note.personID) || [];
                notes.push(note);
                result.set(note.personID, notes);
            });
        }
        return result;
    }, [canReadNotes, notesQuery.data]);
    const filtered = students.filter(student => `${student.Person?.firstName || ''} ${student.Person?.lastName || ''}`.toLowerCase().includes(search.trim().toLowerCase()));
    const refresh = () => queryClient.invalidateQueries({ queryKey: ['bibleStudies'] });

    return (
        <View testID="BibleStudyStudents" style={styles.container}>
            <View style={styles.toolbar}>
                <View style={styles.titleRow}>
                    <Text style={styles.title}>Students</Text>
                    <Text style={styles.total}>{students.length} current and past participants</Text>
                </View>
                <View style={styles.searchBox}>
                    <Unicon name="search-outline" size={18} colorVariant="textSecondary" />
                    <TextInput testID="BibleStudyStudentsSearch" accessibilityLabel="Find a student" placeholder="Find a student" value={search} onChangeText={setSearch} style={styles.searchInput} />
                </View>
            </View>
            {studentsQuery.isError ? (
                <View style={styles.emptyState}>
                    <Text style={styles.empty}>Could not load student attendance.</Text>
                    <ActionButton testID="BibleStudyStudentsRetry" text="Try again" onPress={() => studentsQuery.refetch()} />
                </View>
            ) : (
                <LegendList
                    data={filtered}
                    keyExtractor={student => student.personID}
                    contentContainerStyle={styles.list}
                    estimatedItemSize={340}
                    keyboardShouldPersistTaps="handled"
                    refreshing={studentsQuery.isFetching || notesQuery.isFetching}
                    onRefresh={refresh}
                    renderItem={({ item }) => (
                        <BibleStudyStudentCard
                            student={item}
                            notes={notesByPerson.get(item.personID) || []}
                            loggedInUser={loggedInUser}
                            canReadNotes={canReadNotes}
                            loadingNotes={notesQuery.isLoading}
                            notesError={notesQuery.isError}
                        />
                    )}
                    ListEmptyComponent={<Text style={[styles.empty, styles.emptyState]}>{studentsQuery.isLoading ? 'Loading students...' : search ? 'No students match your search.' : 'Students appear here when added to the study or recorded in a session.'}</Text>}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1 },
    toolbar: { paddingHorizontal: 18, paddingTop: 18, paddingBottom: 12, gap: 14 },
    titleRow: { flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 },
    title: { fontFamily: theme.fonts.heading, fontSize: 22, color: theme.colors.text },
    total: { fontFamily: theme.fonts.body, fontSize: 12, color: theme.colors.textSecondary },
    searchBox: { flexDirection: 'row', gap: 10, alignItems: 'center', backgroundColor: theme.colors.surface, borderRadius: 8, paddingHorizontal: 12 },
    searchInput: { flex: 1, minWidth: 0, paddingVertical: 12, fontSize: 14, fontFamily: theme.fonts.body, color: theme.colors.text },
    list: { paddingHorizontal: 18, paddingTop: 4, paddingBottom: 100 },
    card: { boxShadow: theme.shadows.card, marginBottom: 18 },
    cardContent: { padding: 20, gap: 18 },
    header: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingRight: 8 },
    identity: { flex: 1, gap: 4, minWidth: 0 },
    name: { fontFamily: theme.fonts.heading, fontSize: 18, color: theme.colors.text, _web: { _hover: { color: theme.colors.groups } } },
    membership: { fontFamily: theme.fonts.body, fontSize: 12, color: theme.colors.textSecondary },
    noteCount: { paddingVertical: 5, paddingHorizontal: 9, borderRadius: 5, backgroundColor: theme.colors.groupsContainer },
    noteCountText: { fontFamily: theme.fonts.bold, fontSize: 11, color: theme.colors.groups },
    attendance: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, padding: 14, borderRadius: 6, backgroundColor: theme.colors.surfaceVariant },
    stat: { flexGrow: 1, flexBasis: 100, gap: 5 },
    statValue: { fontFamily: theme.fonts.heading, fontSize: 15, color: theme.colors.text },
    statLabel: { fontFamily: theme.fonts.body, fontSize: 11, color: theme.colors.textSecondary },
    notes: { gap: 14 },
    notesHeading: { flexDirection: 'row', gap: 7, alignItems: 'center' },
    notesLabel: { fontFamily: theme.fonts.bold, fontSize: 12, color: theme.colors.textSecondary },
    timelineEntry: { marginLeft: 6, paddingLeft: 18, paddingBottom: 4, borderLeftWidth: 1, borderLeftColor: theme.colors.surfaceVariant },
    timelineDot: { position: 'absolute', left: -3, top: 7, width: 5, height: 5, borderRadius: 3, backgroundColor: theme.colors.groups },
    empty: { fontFamily: theme.fonts.body, fontSize: 13, lineHeight: 20, color: theme.colors.textSecondary },
    emptyState: { padding: 24, gap: 12, alignItems: 'flex-start' },
}));

export default BibleStudyStudents;
